import type {
  ReportCollection,
  ReportElementType,
  ReportImage,
} from '@highimpact-frontend/api-types';

// -- type definitions --

import type { LegacyDomExportContext } from '../runtime/instanceRegistry/types';

// -- functions --

/**
 * Export a remote widget using the legacy remote function.
 * 
 * Support is maintained until plugins adopt instanceRegistry.
 */
async function exportRemoteWidget(
  target: Element,
  context: LegacyDomExportContext
) {
  const remoteName = target.getAttribute('data-remote-name');
  const remoteUrl = target.getAttribute('data-remote-url');
  const moduleName = target.getAttribute('data-module-name');
  if (!(remoteName && remoteUrl && moduleName)) {
    return null;
  }
  if (!context.executeRemoteFunction) {
    return null;
  }
  return (await context.executeRemoteFunction({
    remoteName,
    remoteUrl,
    moduleName,
    args: [target],
  })) as string;
}

/**
 * Legacy DOM export fallback.
 * 
 * Support is maintained for remote widgets only until plugins adopt instanceRegistry.
 */
export async function legacyDomExportElement(
  element: ReportElementType,
  context: LegacyDomExportContext
): Promise<string | null | undefined> {
  if (element.element_type === 'collection') {
    return null;
  }

  if (element.element_type !== 'remote') {
    return undefined;
  }

  const component = document.querySelector(
    `#widget-${element.template_variable}`
  );

  if (!component) {
    return undefined;
  }

  return exportRemoteWidget(component, context);
}

// -- helper functions --

/**
 * Check if an element is an image collection.
 */
const isImageCollection = (
  element: ReportElementType
): element is ReportCollection =>
  element.element_type === 'collection' &&
  element.elements.length === 1 &&
  element.elements[0]!.element_type === 'image';

function transformGalleriesIntoImageCollections(
  elements: ReportElementType[]
): ReportElementType[] {
  const acc: ReportElementType[] = [];
  elements.forEach((element) => {
    if (!isImageCollection(element)) {
      acc.push(element);
      return;
    }
    const newCollection: ReportCollection = JSON.parse(JSON.stringify(element));
    const htmlElements = document.querySelectorAll(
      `#widget-${element.elements[0]!.template_variable} img`
    );
    const imageElements = Array.from(htmlElements).map(
      (_, i) =>
        ({
          id: 0,
          name: element.name + i,
          template_variable: element.elements[0]!.template_variable,
          element_type: 'image',
          export_type: 'png',
          data_source: [],
        }) as ReportImage
    );
    newCollection.elements = imageElements;
    acc.push(newCollection);
  });
  return acc;
}

/**
 * Apply any transformations necessary to the structure before exporting.
 * 
 * For now, the only transformation applied is to convert image galleries into image collections.
 * This is necessary to insert an arbitrary number of images into the report
 * in a comprehensible way for the report engine.
 */
export function preprocessStructure(
  elements: ReportElementType[]
): ReportElementType[] {
  return [transformGalleriesIntoImageCollections].reduce(
    (acc, f) => f(acc),
    elements
  );
}

/**
 * Flatten the (possibly) nested structure into a flat list of exportable elements.
 */
export function flattenExportableElements(
  elements: ReportElementType[]
): ReportElementType[] {
  const leaves: ReportElementType[] = [];

  const walk = (nodes: ReportElementType[]) => {
    nodes.forEach((element) => {
      if (element.element_type === 'collection') {
        walk(element.elements);
        return;
      }
      leaves.push(element);
    });
  };

  walk(elements);
  return leaves;
}
